import container from '../../../../container';
import { useQuery } from '@tanstack/react-query';
import { useEffect } from 'react';
import { useSelectedShopId } from '../../../../hooks/useSelectedShop';
import { ClaimContext, ClaimType } from '../types';
import { Shipment } from '../../../../api/types';
import { getClaimTypeRequirements } from '../claimsHelpers';

type OnChangeFn = (payload: Record<string, unknown>) => void;

const orderItemsClaimTypes = [ClaimType.MISSING_ITEM, ClaimType.PARTIAL_DAMAGE];

export function useIsOrderItemsRequired(claim: ClaimContext, onChange: OnChangeFn) {
  const { orderId, type: claimType, isReturnShipment, isMultiShipmentOrder, claimRequirements } = claim;
  const shopId = useSelectedShopId();
  const isNonManualClaim = !!orderId;

  const { isLoading: queryIsLoading, data: shipmentData } = useQuery({
    queryKey: ['getMultiShipmentOrder', shopId, orderId],
    queryFn: async () => {
      if (orderId) {
        return container.touchpointsApi.getShipments(shopId, orderId);
      }
    },
    enabled: isNonManualClaim,
  });

  useEffect(() => {
    const isClaimTypeThatRequiresOrderItems = claimType ? orderItemsClaimTypes.includes(claimType) : false;
    const orderItemsRequirement = getClaimTypeRequirements(claimRequirements, claimType)?.orderItems;

    if (isNonManualClaim && shipmentData) {
      // For non manual claims we check shipments to see if it is a multi shipment order

      const outboundShipments = shipmentData.filter((shipment: Shipment) => !shipment.isReturnShipment);
      const isMultiShipmentOrder = outboundShipments.length > 1;
      onChange({
        isMultiShipmentOrder,
        // for shipment based claims the backend already derives the orderItems requirement
        // (incl. return shipments and multi shipment orders), the client-side computation is
        // kept as fallback for claim types without a matrix entry (cash on delivery)
        isOrderItemsRequired: orderItemsRequirement
          ? orderItemsRequirement === 'required'
          : isReturnShipment || isMultiShipmentOrder || isClaimTypeThatRequiresOrderItems,
      });
    } else {
      // For manual claims isMultiShipmentOrder is a field which is set in UI *after* claim
      // requirements are fetched (manual-deadline-check step), so the orderItems requirement
      // has to stay computed on the client to pick up the fresh checkbox value
      onChange({
        // todo: isOrderItemsRequired is a computed field and could theoretically be de-synced, move it outside of state
        isOrderItemsRequired: isReturnShipment || isMultiShipmentOrder || isClaimTypeThatRequiresOrderItems,
      });
    }
  }, [shipmentData, isReturnShipment, isMultiShipmentOrder, claimType, claimRequirements, onChange, isNonManualClaim]);

  // Only consider it loading if the query is enabled and actually loading
  // The loading flag defaults to true, even when the query is not enabled.
  // Which results in an infinite loading screen for manual claims
  const isLoading = !!orderId && queryIsLoading;

  return { isLoading };
}
